import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getKycInfo, submitKyc } from '@/api';
import type { KycInfo } from '@/api';

interface FormErrors {
  frontImage?: string;
  backImage?: string;
  realName?: string;
  idCard?: string;
}

const emptyForm = {
  frontImage: '',
  backImage: '',
  realName: '',
  idCard: '',
};

const instructions = [
  '请上传身份证正反面照片，确保信息清晰完整',
  '请核对姓名和证件号，与证件保持一致',
  '实名信息经过加密保存，不会对外泄露',
];

export default function VerifyPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<KycInfo | null>(null);
  const [infoError, setInfoError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg(msg);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2000);
  }, []);

  //加载实名认证状态
  useEffect(() => {
    let mounted = true;
    getKycInfo()
      .then((res) => {
        if (!mounted) return;
        setInfo(res);
        //已提交过：回显姓名和身份证号
        if (res.realName || res.idCard) {
          setForm((prev) => ({ ...prev, realName: res.realName, idCard: res.idCard }));
        }
      })
      .catch((err) => {
        if (mounted) setInfoError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    if (!form.frontImage) {
      newErrors.frontImage = '请上传身份证正面照片';
    }
    if (!form.backImage) {
      newErrors.backImage = '请上传身份证背面照片';
    }
    if (!form.realName.trim()) {
      newErrors.realName = '请输入真实姓名';
    }
    if (!form.idCard.trim()) {
      newErrors.idCard = '请输入身份证号码';
    } else if (!/^\d{17}[\dXx]$/.test(form.idCard.trim())) {
      newErrors.idCard = '请输入正确的18位身份证号码';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleImageChange = useCallback(
    (field: 'frontImage' | 'backImage', e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setForm((prev) => ({ ...prev, [field]: ev.target?.result as string }));
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        };
        reader.readAsDataURL(file);
      }
    },
    []
  );

  //提交实名认证
  const handleSubmit = useCallback(async () => {
    if (!validate() || loading) return;
    setLoading(true);
    try {
      await submitKyc(form.frontImage, form.backImage, form.realName.trim(), form.idCard.trim());
      showToast('实名认证提交成功，请耐心等待审核');
      setTimeout(() => navigate('/profile'), 1500);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '提交失败');
    } finally {
      setLoading(false);
    }
  }, [validate, loading, form, showToast, navigate]);

  const ImageUploadBox = useCallback(
    ({
      label,
      required,
      field,
      inputRef,
    }: {
      label: string;
      required: boolean;
      field: 'frontImage' | 'backImage';
      inputRef: React.RefObject<HTMLInputElement | null>;
    }) => {
      const hasImage = !!form[field];
      return (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-id-card-line text-red-500 text-lg"></i>
            <span className="text-sm font-medium text-foreground-900">
              {label}
            </span>
            {required && <span className="text-red-500 text-sm">*</span>}
          </div>

          <div
            className={`relative w-full rounded-xl border-2 border-dashed ${
              hasImage ? 'border-red-300 bg-red-50/50' : 'border-background-300 bg-white'
            } transition-colors overflow-hidden`}
            style={{ minHeight: 160 }}
          >
            {hasImage ? (
              <div className="relative w-full" style={{ minHeight: 160 }}>
                <img
                  src={form[field]}
                  alt={label}
                  className="w-full h-40 object-cover"
                />
                <button
                  onClick={() => setForm((prev) => ({ ...prev, [field]: '' }))}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center text-sm cursor-pointer hover:bg-black/70 transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center py-10 px-6 cursor-pointer"
                onClick={() => inputRef.current?.click()}
              >
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
                  <i className="ri-camera-line text-red-500 text-xl"></i>
                </div>
                <p className="text-sm text-foreground-500 text-center">
                  {field === 'frontImage'
                    ? '请拍照或从相册选取正面'
                    : '拍照或从相册选取背面'}
                </p>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleImageChange(field, e)}
            />
          </div>

          {errors[field] && (
            <p className="text-red-500 text-xs mt-1.5">{errors[field]}</p>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={() => inputRef.current?.click()}
              className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium cursor-pointer hover:bg-red-600 transition-colors active:scale-[0.98] flex items-center justify-center gap-1.5"
            >
              <i className="ri-camera-line text-sm"></i>
              拍照
            </button>
            <button
              onClick={() => inputRef.current?.click()}
              className="flex-1 py-2.5 rounded-lg border border-red-300 text-red-500 text-sm font-medium cursor-pointer hover:bg-red-50 transition-colors active:scale-[0.98] flex items-center justify-center gap-1.5"
            >
              <i className="ri-image-line text-sm"></i>
              从相册选择
            </button>
          </div>
        </div>
      );
    },
    [form, errors, handleImageChange]
  );

  //状态视图：待审核
  if (info && info.realnameStatus === 1) {
    return (
      <div className="mobile-container min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-white border-b border-background-200/60">
          <div className="flex items-center px-3 h-12">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="flex-1 text-center text-base font-medium text-foreground-900 pr-8">
              实名认证
            </h1>
          </div>
        </header>
        <main className="px-6 py-16 flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <i className="ri-time-line text-amber-500 text-3xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">审核中</h2>
          <p className="text-sm text-foreground-400 text-center leading-relaxed">
            您的实名认证正在审核中
            <br />
            请耐心等待，审核结果将第一时间通知您
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="mt-8 px-8 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
          >
            返回个人中心
          </button>
        </main>
      </div>
    );
  }

  //状态视图：已认证
  if (info && info.realnameStatus === 2) {
    return (
      <div className="mobile-container min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-white border-b border-background-200/60">
          <div className="flex items-center px-3 h-12">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="flex-1 text-center text-base font-medium text-foreground-900 pr-8">
              实名认证
            </h1>
          </div>
        </header>
        <main className="px-6 py-16 flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <i className="ri-shield-check-fill text-green-500 text-3xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">已完成实名认证</h2>
          {info.realName && (
            <p className="text-sm text-foreground-500">
              认证人：{info.realName}
            </p>
          )}
          <button
            onClick={() => navigate('/profile')}
            className="mt-8 px-8 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
          >
            返回个人中心
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen bg-white">
      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[100] bg-foreground-900/90 text-white text-sm px-4 py-2.5 rounded-full shadow-lg animate-[fadeIn_0.2s_ease-out]">
          {toastMsg}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-background-200/60">
        <div className="flex items-center px-3 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
          </button>
          <h1 className="flex-1 text-center text-base font-medium text-foreground-900 pr-8">
            实名认证
          </h1>
        </div>
      </header>

      <main className="px-4 py-6">
        {/* Icon + Title */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <i className="ri-shield-check-line text-red-500 text-3xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900">实名身份认证</h2>
          <p className="text-sm text-foreground-500 mt-1">
            为保障交易安全，请完成真实身份核验
          </p>
        </div>

        {/* 拒绝原因提示（被拒绝后可重新提交） */}
        {info && info.realnameStatus === 3 && info.rejectReason && (
          <div className="bg-red-50 rounded-xl border border-red-200/60 p-4 mb-4">
            <p className="text-sm font-medium text-red-600 mb-1">认证未通过</p>
            <p className="text-xs text-red-500 leading-relaxed">原因：{info.rejectReason}</p>
            <p className="text-xs text-foreground-400 mt-1.5">请修改后重新提交</p>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-amber-50 rounded-xl border border-amber-200/60 p-4 mb-6">
          <p className="text-sm font-medium text-amber-700 mb-2">操作说明</p>
          <ul className="space-y-1.5">
            {instructions.map((text, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0"></span>
                <span className="text-xs text-amber-700 leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Front ID */}
        <ImageUploadBox
          label="身份证正面（头像面）"
          required
          field="frontImage"
          inputRef={frontInputRef}
        />

        {/* Back ID */}
        <div className="mt-6">
          <ImageUploadBox
            label="身份证背面（国徽面）"
            required
            field="backImage"
            inputRef={backInputRef}
          />
        </div>

        {/* Real Name */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-foreground-700 mb-2">
            真实姓名
          </label>
          <input
            type="text"
            placeholder="请输入身份证上的真实姓名"
            value={form.realName}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, realName: e.target.value }));
              setErrors((prev) => ({ ...prev, realName: undefined }));
            }}
            className={`w-full px-4 py-3 rounded-lg border text-sm text-foreground-900 placeholder:text-foreground-400 bg-white outline-none transition-colors ${
              errors.realName
                ? 'border-red-400 focus:border-red-500'
                : 'border-background-200 focus:border-red-400'
            }`}
          />
          {errors.realName && (
            <p className="text-red-500 text-xs mt-1.5">{errors.realName}</p>
          )}
        </div>

        {/* ID Card Number */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-foreground-700 mb-2">
            身份证号码
          </label>
          <input
            type="text"
            maxLength={18}
            placeholder="请输入18位身份证号码"
            value={form.idCard}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, idCard: e.target.value }));
              setErrors((prev) => ({ ...prev, idCard: undefined }));
            }}
            className={`w-full px-4 py-3 rounded-lg border text-sm text-foreground-900 placeholder:text-foreground-400 bg-white outline-none transition-colors ${
              errors.idCard
                ? 'border-red-400 focus:border-red-500'
                : 'border-background-200 focus:border-red-400'
            }`}
          />
          {errors.idCard && (
            <p className="text-red-500 text-xs mt-1.5">{errors.idCard}</p>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full mt-8 mb-4 py-3.5 rounded-xl bg-red-500 text-white text-base font-medium cursor-pointer hover:bg-red-600 transition-colors active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              提交中...
            </>
          ) : (
            <>
              <i className="ri-shield-check-line text-lg"></i>
              提交实名认证
            </>
          )}
        </button>

        {/* 状态加载失败提示 */}
        {infoError && (
          <p className="text-center text-xs text-foreground-400">{infoError}（不影响首次提交）</p>
        )}
      </main>
    </div>
  );
}
